import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, Users, Search, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";
import CsvImport from "@/components/CsvImport";

function PartyModal({ open, onClose, onSaved, defaultType, initial }) {
  const empty = {
    name: "", type: defaultType || "customer", gstin: "", tax_id: "", email: "", phone: "",
    billing_address: "", state: "", state_code: "", country: "", opening_balance: 0,
  };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (initial) {
      // Populate from existing party — keep unknown fields intact for round-trip.
      setF({ ...empty, ...initial });
    } else {
      setF({ ...empty, type: defaultType || "customer" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultType, initial]);
  if (!open) return null;
  const isEdit = Boolean(initial?.id);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/parties/${initial.id}`, f);
        toast.success("Saved");
      } else {
        await api.post("/parties", f);
        toast.success(`${f.type === "customer" ? "Customer" : "Vendor"} added`);
      }
      onSaved(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="party-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">{isEdit ? `Edit ${f.name || (f.type === "customer" ? "Customer" : "Vendor")}` : `New ${f.type === "customer" ? "Customer" : "Vendor"}`}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className="label-field">Type</label>
            <select className="input-clean" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
              <option value="customer">Customer</option><option value="vendor">Vendor</option>
            </select></div>
          <div><label className="label-field">Name *</label>
            <input data-testid="party-name" className="input-clean" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}/></div>
          <div><label className="label-field">GSTIN</label>
            <input data-testid="party-gstin" className="input-clean font-mono-data" value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })}/></div>
          <div><label className="label-field">Tax ID (non-India)</label>
            <input className="input-clean font-mono-data" value={f.tax_id} onChange={(e) => setF({ ...f, tax_id: e.target.value })}/></div>
          <div><label className="label-field">Email</label>
            <input className="input-clean" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}/></div>
          <div><label className="label-field">Phone</label>
            <input className="input-clean" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}/></div>
          <div><label className="label-field">State</label>
            <input className="input-clean" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })}/></div>
          <div><label className="label-field">State Code (2 letters)</label>
            <input className="input-clean font-mono-data uppercase" maxLength={2} value={f.state_code} onChange={(e) => setF({ ...f, state_code: e.target.value.toUpperCase() })}/></div>
          <div><label className="label-field">Country</label>
            <input className="input-clean" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })}/></div>
          <div><label className="label-field">Opening Balance</label>
            <input type="number" step="0.01" className="input-clean font-mono-data" value={f.opening_balance} onChange={(e) => setF({ ...f, opening_balance: parseFloat(e.target.value) || 0 })}/></div>
          <div className="md:col-span-2"><label className="label-field">Billing Address</label>
            <textarea className="input-clean" rows={2} value={f.billing_address} onChange={(e) => setF({ ...f, billing_address: e.target.value })}/></div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-ghost w-full sm:w-auto justify-center">Cancel</button>
          <button data-testid="party-save-btn" onClick={save} disabled={saving} className="btn-primary w-full sm:w-auto justify-center">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Parties() {
  const { active } = useCompany();
  const [search] = useSearchParams();
  const initialType = search.get("type") || "customer";
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState(initialType);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/parties?type=${tab}`);
      setRows(data.parties || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, tab]);

  const removeParty = async (id) => {
    if (!window.confirm("Delete this party?")) return;
    try { await api.delete(`/parties/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="parties-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Parties</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">Customers & Vendors</h1>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-party-btn" className="btn-primary w-full sm:w-auto justify-center">
          <Plus size={14}/> New {tab === "customer" ? "Customer" : "Vendor"}
        </button>
      </div>

      <div className="flex gap-2">
        <button data-testid="tab-customer" onClick={() => setTab("customer")}
          className={`px-4 py-2 text-sm font-medium rounded ${tab === "customer" ? "bg-[#1E3A8A] text-white" : "bg-white border border-[#E2E8F0]"}`}>Customers</button>
        <button data-testid="tab-vendor" onClick={() => setTab("vendor")}
          className={`px-4 py-2 text-sm font-medium rounded ${tab === "vendor" ? "bg-[#1E3A8A] text-white" : "bg-white border border-[#E2E8F0]"}`}>Vendors</button>
      </div>

      <div className="card-surface p-4">
        <div className="flex items-center gap-2 mb-4">
          <Search size={16} className="text-[#94A3B8]"/>
          <input className="input-clean" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 400 }}/>
        </div>
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-[#CBD5E1] mb-3"/>
            <p className="text-[#64748B]">No {tab}s yet.</p>
          </div>
        ) : (
          <table className="table-clean">
            <thead>
              <tr>
                <th>Name</th><th>GSTIN/Tax ID</th><th>Email</th><th>Phone</th>
                <th className="text-right">Balance</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`party-row-${p.id}`}>
                  <td className="font-medium">{p.name}</td>
                  <td className="font-mono-data text-xs">{p.gstin || p.tax_id || "-"}</td>
                  <td className="text-sm">{p.email || "-"}</td>
                  <td className="text-sm font-mono-data">{p.phone || "-"}</td>
                  <td className="text-right font-mono-data">{fmtMoney(p.balance, active?.base_currency)}</td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`edit-party-${p.id}`}
                        onClick={() => { setEditing(p); setOpen(true); }}
                        className="text-[#1E3A8A] hover:text-[#0F172A]"
                        title="Edit"
                      ><Pencil size={14}/></button>
                      <button
                        data-testid={`delete-party-${p.id}`}
                        onClick={() => removeParty(p.id)}
                        className="text-[#DC2626]"
                        title="Delete"
                      ><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <PartyModal
        open={open}
        onClose={() => { setOpen(false); setEditing(null); }}
        onSaved={load}
        defaultType={tab}
        initial={editing}
      />
    </div>
  );
}
